import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Box, Grid, Stack, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { DialogDefaultV2 } from '../components/v2';
import { geoPointsFromItens, horariosBannerFromItens } from './jornadaHelpers';
import { fixLeafletIcon, MAP_SETTINGS } from '../utils/nsirMapConfig';

fixLeafletIcon();

const MAP_HEIGHT = 420;
const FOCUS_ZOOM = 16;
const MAP_RADIUS = 0.5;

const buildPopupHtml = (point) => {
  const titleLine = `${point.title} - ${point.horario || '—'}`;
  if (!point.endereco) return titleLine;
  return `${titleLine}<br/><span class="jornada-map-popup-address">${point.endereco}</span>`;
};

const JornadaMapHorariosBanner = ({ rows, onSelect }) => (
  <Box
    sx={(theme) => ({
      border: `1px solid ${alpha(theme.palette.divider, 0.8)}`,
      borderRadius: MAP_RADIUS,
      px: 1.5,
      py: 1,
      backgroundColor: alpha(theme.palette.background.default, 0.6),
    })}
  >
    <Grid container spacing={1}>
      {rows.map((row) => (
        <Grid key={row.tipo} size={{ xs: 6, sm: 3 }}>
          <Box
            onClick={row.hasGeo ? () => onSelect(row.tipo) : undefined}
            role={row.hasGeo ? 'button' : undefined}
            tabIndex={row.hasGeo ? 0 : undefined}
            onKeyDown={row.hasGeo ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') onSelect(row.tipo);
            } : undefined}
            sx={(theme) => ({
              borderRadius: MAP_RADIUS,
              cursor: row.hasGeo ? 'pointer' : 'default',
              opacity: row.hasGeo ? 1 : 0.5,
              px: 1,
              py: 0.5,
              transition: theme.transitions.create(['background-color', 'box-shadow'], {
                duration: theme.transitions.duration.shortest,
              }),
              ...(row.hasGeo && {
                '&:hover': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.08),
                },
              }),
            })}
          >
            <Typography variant="caption" color="text.secondary" noWrap display="block">
              {row.label}
            </Typography>
            <Typography variant="body2" fontWeight={700} noWrap>
              {row.horario || '—'}
            </Typography>
          </Box>
        </Grid>
      ))}
    </Grid>
  </Box>
);

JornadaMapHorariosBanner.propTypes = {
  rows: PropTypes.arrayOf(PropTypes.shape({
    tipo: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    horario: PropTypes.string,
    hasGeo: PropTypes.bool.isRequired,
  })).isRequired,
  onSelect: PropTypes.func.isRequired,
};

const JornadaMapCanvas = ({ points, open, focusTipo, onFocusComplete }) => {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const markersByTipoRef = useRef({});

  useEffect(() => {
    if (!open) return undefined;
    const el = containerRef.current;
    if (!el || mapRef.current) return undefined;

    const map = L.map(el, {
      center: MAP_SETTINGS.center,
      zoom: MAP_SETTINGS.initialZoom,
      minZoom: MAP_SETTINGS.minZoom,
      maxZoom: MAP_SETTINGS.maxZoom,
      attributionControl: false,
    });

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '',
    }).addTo(map);

    mapRef.current = map;

    const resizeObserver = new ResizeObserver(() => {
      map.invalidateSize();
    });
    resizeObserver.observe(el);

    return () => {
      resizeObserver.disconnect();
      map.remove();
      mapRef.current = null;
      markersByTipoRef.current = {};
    };
  }, [open]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !open) return;

    Object.values(markersByTipoRef.current).forEach((marker) => marker.remove());
    markersByTipoRef.current = {};

    if (!points.length) return;

    const bounds = L.latLngBounds([]);
    points.forEach((point) => {
      const marker = L.marker([point.lat, point.lng]).addTo(map);
      marker.bindPopup(buildPopupHtml(point), {
        className: 'jornada-map-popup',
        closeButton: true,
      });
      markersByTipoRef.current[point.tipo] = marker;
      bounds.extend([point.lat, point.lng]);
    });

    requestAnimationFrame(() => {
      map.invalidateSize();
      if (points.length === 1) {
        map.setView([points[0].lat, points[0].lng], FOCUS_ZOOM);
      } else {
        map.fitBounds(bounds, { padding: [40, 40] });
      }
    });
  }, [points, open]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !open || !focusTipo) return;

    const marker = markersByTipoRef.current[focusTipo];
    if (!marker) {
      onFocusComplete?.();
      return;
    }

    map.flyTo(marker.getLatLng(), FOCUS_ZOOM, { duration: 0.4 });
    marker.openPopup();
    onFocusComplete?.();
  }, [focusTipo, open, onFocusComplete]);

  return (
    <Box
      ref={containerRef}
      sx={(theme) => ({
        height: MAP_HEIGHT,
        width: '100%',
        '& .leaflet-container': {
          height: '100%',
          width: '100%',
          borderRadius: MAP_RADIUS,
        },
        '& .leaflet-control-attribution': {
          display: 'none',
        },
        '& .jornada-map-popup .leaflet-popup-content-wrapper': {
          backgroundColor: theme.palette.background.paper,
          borderRadius: 2,
          boxShadow: theme.shadows[2],
        },
        '& .jornada-map-popup .leaflet-popup-content': {
          color: theme.palette.text.primary,
          fontSize: '12px',
          fontWeight: 600,
          lineHeight: 1.35,
          margin: '8px 10px',
          maxWidth: 260,
          whiteSpace: 'normal',
        },
        '& .jornada-map-popup .jornada-map-popup-address': {
          color: theme.palette.text.secondary,
          display: 'block',
          fontSize: '11px',
          fontWeight: 400,
          marginTop: '4px',
        },
        '& .jornada-map-popup .leaflet-popup-tip': {
          backgroundColor: theme.palette.background.paper,
        },
      })}
    />
  );
};

JornadaMapCanvas.propTypes = {
  points: PropTypes.arrayOf(PropTypes.shape({
    tipo: PropTypes.string.isRequired,
    lat: PropTypes.number.isRequired,
    lng: PropTypes.number.isRequired,
    title: PropTypes.string.isRequired,
    horario: PropTypes.string,
    endereco: PropTypes.string,
  })).isRequired,
  open: PropTypes.bool.isRequired,
  focusTipo: PropTypes.string,
  onFocusComplete: PropTypes.func,
};

const JornadaPontosMap = ({ open, onClose, itens }) => {
  const [focusTipo, setFocusTipo] = useState(null);
  const points = useMemo(() => geoPointsFromItens(itens), [itens]);
  const schedule = useMemo(() => horariosBannerFromItens(itens), [itens]);

  useEffect(() => {
    if (!open) setFocusTipo(null);
  }, [open]);

  return (
    <DialogDefaultV2
      open={open}
      handlerCancel={onClose}
      title="Mapa da jornada"
      maxWidth="md"
      fullWidth
      readOnly
    >
      {open && points.length > 0 ? (
        <Stack spacing={1.5}>
          <JornadaMapHorariosBanner
            rows={schedule}
            onSelect={setFocusTipo}
          />
          <JornadaMapCanvas
            points={points}
            open={open}
            focusTipo={focusTipo}
            onFocusComplete={() => setFocusTipo(null)}
          />
        </Stack>
      ) : null}
    </DialogDefaultV2>
  );
};

JornadaPontosMap.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  itens: PropTypes.array,
};

JornadaPontosMap.defaultProps = {
  itens: [],
};

export default JornadaPontosMap;
